import { useContext, useMemo } from 'react';
import { Box, Tabs, Tab, IconButton } from '@mui/material';
import { ArrowBack, ArrowForward, CalendarMonth, Check, DirectionsCarFilledOutlined } from '@mui/icons-material';
import { TabValue } from '@/types';
import { ctxRefuel } from '@/contexts/RefuelsCtx';
import { useTranslation } from 'react-i18next';
import { datePeriodMD } from '@/utilities/dateUtils';

export function RefuelHeader() {
  const ctx = useContext(ctxRefuel);
  const { t, i18n } = useTranslation();

  const hasDates = Boolean(ctx?.selectedDates?.from && ctx.selectedDates?.to);
  const intervalLength = useMemo(() => {
    if (hasDates && ctx) {
      return datePeriodMD(ctx.selectedDates!.from, ctx.selectedDates!.to, t);
    }
    return '';
  }, [ctx?.selectedDates?.from, ctx?.selectedDates?.to, i18n, hasDates]);

  if (!ctx) return null;

  const hasRefuels = (ctx.refuels?.length ?? 0) > 0;
  const onResultTab = ctx.tab === 2;
  const canGoForward = hasRefuels && hasDates && !onResultTab && !ctx.loading;

  function navigateBack() {
    ctx?.setTab(ctx.tab - 1);
  }

  function navigateForward() {
    if (hasRefuels && !ctx?.loading) {
      ctx?.setTab(2);
    }
  }

  return (
    <>
      <div className="refuel-summary-detail-header">
        {onResultTab && (
          <span className="left">
            <IconButton data-qa-element-id="back_button" onClick={navigateBack} sx={{ color: 'text.primary' }}>
              <ArrowBack />
            </IconButton>
          </span>
        )}
        <div className="refuel-tab-name">
          {ctx.selectedTarget && onResultTab ? (
            <div className="refuel-tab-name-target">
              <div data-qa-element-id="refuel_result_target_name">{ctx.selectedTarget.name}</div>
              {ctx.selectedTarget?.description && (
                <div className="refuel-tab-description" data-qa-element-id="refuel_result_target_description">
                  {ctx.selectedTarget.description}
                </div>
              )}
            </div>
          ) : (
            <div data-qa-element-id="refuel_tab_title">{t('mainMenu.refuels')}</div>
          )}
        </div>
        {canGoForward && (
          <span className="right">
            <IconButton data-qa-element-id="forward_button" onClick={navigateForward} sx={{ color: 'text.primary' }}>
              <ArrowForward />
            </IconButton>
          </span>
        )}
      </div>
      {!onResultTab && (
        <Box className="refuel-header">
          <Tabs
            variant="fullWidth"
            value={ctx.tab}
            indicatorColor="secondary"
            textColor="secondary"
            onChange={(_evt, value) => ctx.setTab(value as TabValue)}
          >
            <Tab
              data-qa-element-id="target_selector_tab"
              value={TabValue.targetSelect}
              icon={
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DirectionsCarFilledOutlined />
                  {ctx.selectedTarget && (
                    <Check color="success" fontSize="medium" sx={{ position: 'absolute', right: -25 }} />
                  )}
                </Box>
              }
              label={ctx.selectedTarget?.name ?? t('refuels.selectVehicle')}
              sx={{ textTransform: 'none' }}
            />
            <Tab
              data-qa-element-id="date_selector_tab"
              value={TabValue.dateSelect}
              icon={<CalendarMonth />}
              label={
                <div
                  data-qa-element-id={`date_selector_tab_label_${ctx.selectedDates?.from.toLocaleDateString()}_${ctx.selectedDates?.to.toLocaleDateString()}`}
                >
                  {intervalLength || t('refuels.period')}
                </div>
              }
              sx={{ opacity: !ctx.selectedTarget ? 0 : 1, textTransform: 'none' }}
              disabled={!ctx.selectedTarget}
            />
          </Tabs>
        </Box>
      )}
    </>
  );
}
